import { inject, Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { ApiResponse } from '../../../types/commons';
import { RuntimeConfigService } from '../common/runtime-config.service';

/**
 * Outcome of a current-password check.
 *
 * `unknown` exists so a dropped request is never rendered as a wrong password.
 * Accusing a user of mistyping a password they typed correctly is worse than
 * staying silent — callers render nothing for `unknown` and let the save path
 * report the real failure.
 */
export type CurrentPasswordVerdict = 'valid' | 'invalid' | 'unknown';

/**
 * Read-only check of the password typed into the My Profile "Current Password"
 * field against the caller's stored hash.
 *
 * Deliberately its own service rather than another method on the 590-line
 * `AuthService`: one endpoint, one public method, no shared state — the "small,
 * obvious seams" rule in the root CLAUDE.md.
 *
 * Backed by `POST /api/user/verify-current-password`, which answers a wrong
 * password with **200 + data:false** rather than a 4xx, so "wrong password" is
 * never confused with "request failed".
 *
 * **It lives under `/api/user/` rather than the more obvious `/api/auth/`, and
 * that placement is load-bearing.** `csrfInterceptor` deliberately
 * skips every path under `/api/auth/`, so an endpoint parked there gets no
 * `X-XSRF-TOKEN`; the backend's global antiforgery filter then rejects it with
 * **401**, which is indistinguishable from "not logged in". This endpoint
 * originally shipped under `/api/auth/` and 401'd on every call for exactly
 * that reason. Its sibling `POST /api/user/email-availability` — the other
 * async field check on this same form — is the pattern being followed.
 */
@Injectable({ providedIn: 'root' })
export class CurrentPasswordVerificationService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(RuntimeConfigService);
  private readonly API_URL = `${this.config.apiUrl()}/user`;

  verify(currentPassword: string): Observable<CurrentPasswordVerdict> {
    return this.http
      .post<ApiResponse<boolean>>(
        `${this.API_URL}/verify-current-password`,
        { currentPassword },
        { withCredentials: true }
      )
      .pipe(
        map((response): CurrentPasswordVerdict => {
          if (!response?.status) {
            return 'unknown';
          }
          return response.data === true ? 'valid' : 'invalid';
        }),
        catchError(() => of<CurrentPasswordVerdict>('unknown'))
      );
  }
}
